import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import Header from "./components/Header";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import CustomerDetail from "./pages/CustomerDetail";
import Jobs from "./pages/Jobs";
import JobDetail from "./pages/JobDetail";
import Quotes from "./pages/Quotes";
import QuoteDetail from "./pages/QuoteDetail";
import DraftQuoteDetail from "./pages/DraftQuoteDetail";
import WorkOrders from "./pages/WorkOrders";
import WorkOrderDetail from "./pages/WorkOrderDetail";
import ShopStatus from "./pages/ShopStatus";
import Settings from "./pages/Settings";
import Employees from "./pages/Employees";
import Inventory from "./pages/Inventory";
import Labor from "./pages/Labor";
import SeedStarterData from "./pages/SeedStarterData";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Documentation from "./pages/Documentation";
import Blueprints from "./pages/Blueprints";
import NewBlueprintSession from "./pages/NewBlueprintSession";

export default function App() {
  const { isLoggedIn, loading, user } = useAuth();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        Loading...
      </div>
    );
  }

  const isWorker = user?.role === "worker";
  const isAdmin = user?.role === "admin";
  const homePath = isWorker ? "/work-orders" : "/";

  return (
    <div className="app">
      {isLoggedIn && <Header />}
      <Routes>
        {/* Public legal pages — accessible whether logged in or not */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        {isLoggedIn ? (
          <>
            {/* Protected Routes */}
            <Route
              path="/"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Dashboard />
              }
            />
            <Route
              path="/dashboard"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Dashboard />
              }
            />
            <Route
              path="/customers"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Customers />
              }
            />
            <Route
              path="/customers/:id"
              element={
                isWorker ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <CustomerDetail />
                )
              }
            />
            <Route
              path="/jobs"
              element={isWorker ? <Navigate to={homePath} replace /> : <Jobs />}
            />
            <Route
              path="/jobs/:id"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <JobDetail />
              }
            />
            <Route
              path="/quotes"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Quotes />
              }
            />
            <Route
              path="/quotes/:id"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <QuoteDetail />
              }
            />
            <Route
              path="/draft-quotes/:id"
              element={
                isWorker ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <DraftQuoteDetail />
                )
              }
            />
            <Route path="/work-orders" element={<WorkOrders />} />
            <Route path="/work-orders/:id" element={<WorkOrderDetail />} />
            <Route path="/shop-status" element={<ShopStatus />} />
            <Route path="/documentation" element={<Documentation />} />
            <Route
              path="/blueprints"
              element={
                isWorker ? (
                  <Navigate to={homePath} replace />
                ) : (
                  <NewBlueprintSession />
                )
              }
            />
            <Route
              path="/blueprints/session"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Blueprints />
              }
            />
            <Route
              path="/inventory"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Inventory />
              }
            />
            <Route
              path="/labor"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Labor />
              }
            />
            <Route
              path="/settings"
              element={
                isWorker ? <Navigate to={homePath} replace /> : <Settings />
              }
            />
            <Route
              path="/employees"
              element={
                isAdmin ? <Employees /> : <Navigate to={homePath} replace />
              }
            />
            {/* Secret, unlinked route — not in Header nav. Seeds starter inventory for new domains. */}
            <Route
              path="/secret-seed-data"
              element={
                isAdmin ? (
                  <SeedStarterData />
                ) : (
                  <Navigate to={homePath} replace />
                )
              }
            />
            {/* Redirect unknown paths to the role's home page */}
            <Route path="*" element={<Navigate to={homePath} replace />} />
          </>
        ) : (
          <>
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            {/* Redirect unknown paths to login */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </Routes>
    </div>
  );
}
